import { Check, Globe, MessageCircle } from "lucide-react";
import { toast } from "sonner";

import type { PlanDto } from "@mandoob/shared";

import { useCheckout, useGetPlans } from "@/api/billing";
import { Currency } from "@/components/shared/currency";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useLanguage } from "@/contexts/language";
import { SUPPORT_WHATSAPP_URL } from "@/features/landing/contact-page";
import { OfferBanner } from "@/features/offer/offer-banner";
import { isNative } from "@/lib/capacitor";
import { formatNumber } from "@/lib/format";
import { errorMessage } from "@/lib/errors";
import { cn } from "@/lib/utils";
import { useSession } from "@/session";

/**
 * Self-serve plan purchase is paused: the in-app page hides the plan catalog
 * and asks drivers to contact support to charge credits instead. Flip to
 * false to bring the plan grid and checkout back (the landing page's public
 * pricing section is unaffected either way).
 */
const PLANS_HIDDEN = true;

/** "Contact us to charge credits" — replaces the plan grid while it's hidden. */
function ContactToBuyCard() {
  const { t } = useLanguage();
  const session = useSession();
  // Prefilled message so support knows who's asking without a back-and-forth.
  const who = session?.driver.name ?? session?.userName ?? "";
  const phone = session?.driver.phone ? `+${session.driver.phone}` : "";
  const text = t("Mandoob:Plans.ContactPrefill", who, phone).trim();
  const href = `${SUPPORT_WHATSAPP_URL}?text=${encodeURIComponent(text)}`;

  // Native: a plain navigation to a foreign host is intercepted by the
  // Capacitor shell and handed to the OS as a VIEW intent, so WhatsApp itself
  // opens. Deliberately NOT the Browser plugin — that would put wa.me's
  // "continue to chat" page inside an in-app Custom Tab. Web: a new tab.
  const openWhatsApp = () => {
    if (isNative()) {
      window.location.assign(href);
      return;
    }
    window.open(href, "_blank", "noopener");
  };

  return (
    <div className="tw:flex tw:flex-col tw:items-start tw:gap-3 tw:rounded-2xl tw:border tw:border-brand/30 tw:bg-brand-50/60 tw:p-5">
      <span className="tw:flex tw:size-10 tw:items-center tw:justify-center tw:rounded-full tw:bg-brand tw:text-brand-foreground">
        <MessageCircle className="tw:size-5" strokeWidth={1.75} />
      </span>
      <h2 className="tw:m-0 tw:text-base tw:font-semibold tw:text-foreground">
        {t("Mandoob:Plans.ContactTitle")}
      </h2>
      <p className="tw:m-0 tw:text-sm tw:leading-relaxed tw:text-foreground/80">
        {t("Mandoob:Plans.ContactText")}
      </p>
      <Button
        type="button"
        onClick={openWhatsApp}
        className="tw:mt-1 tw:w-full tw:gap-2 tw:sm:w-auto"
      >
        <MessageCircle className="tw:size-4.5" strokeWidth={1.75} />
        {t("Mandoob:Plans.ContactButton")}
      </Button>
    </div>
  );
}

function PlanCard({
  plan,
  onBuy,
  buying,
  bestValue,
  accent,
}: {
  plan: PlanDto;
  onBuy: () => void;
  buying: boolean;
  /** The largest plan in the catalog — gets the spotlight. */
  bestValue?: boolean;
  /** Top plan: green corner glow decoration. */
  accent?: boolean;
}) {
  const { t, language } = useLanguage();
  const name = language === "ar" ? plan.nameAr : plan.nameEn;

  // Mirrors the landing page's pricing card, with the charge button pinned to
  // the card's bottom (mt-auto — grid cells stretch to equal height).
  return (
    <div
      className={cn(
        "tw:relative tw:flex tw:flex-col tw:gap-3 tw:rounded-2xl tw:border tw:bg-card tw:p-5",
        bestValue
          ? "tw:border-brand tw:shadow-lg tw:ring-1 tw:ring-brand"
          : "tw:border-border",
      )}
    >
      {accent && (
        <span
          aria-hidden
          className="tw:pointer-events-none tw:absolute tw:inset-0 tw:rounded-2xl tw:bg-linear-to-tl tw:from-success/25 tw:via-success/5 tw:via-35% tw:to-transparent tw:to-60% tw:rtl:bg-linear-to-tr"
        />
      )}
      {bestValue && (
        <span className="tw:absolute tw:-top-3 tw:start-4 tw:whitespace-nowrap tw:rounded-full tw:bg-brand tw:px-3 tw:py-1 tw:text-xs tw:font-bold tw:text-brand-foreground">
          {t("Mandoob:Plans.BestValue")}
        </span>
      )}
      <span className="tw:text-sm tw:font-semibold tw:text-foreground/80">
        {name}
      </span>
      <span className="tw:flex tw:flex-wrap tw:items-baseline tw:gap-1.5">
        <span className="tw:text-sm tw:text-foreground/70">
          {t("Mandoob:Plans.UpTo")}
        </span>
        <span className="tw:text-3xl tw:font-bold tw:tabular-nums tw:text-foreground">
          {formatNumber(plan.credits, language)}
        </span>
        <span className="tw:text-sm tw:text-foreground/70">
          {t("Mandoob:Plans.DeliveriesUnit")}
        </span>
      </span>
      <span className="tw:inline-flex tw:items-center tw:gap-1 tw:text-lg tw:font-semibold tw:tabular-nums tw:text-brand">
        {formatNumber(plan.priceHalalas / 100, language, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
        <Currency currency={plan.currency} className="tw:size-4.5" />
      </span>
      <ul className="tw:m-0 tw:mt-1 tw:flex tw:list-none tw:flex-col tw:gap-2 tw:border-t tw:border-border tw:p-0 tw:pt-3">
        {[
          t(
            "Mandoob:Plans.FeatureCreditsBase",
            formatNumber(plan.credits, language),
          ),
          t("Mandoob:Plans.FeatureWhatsApp"),
          t("Mandoob:Plans.FeatureMedia"),
          t("Mandoob:Plans.FeatureQuickActions"),
          t("Mandoob:Plans.FeatureReplies"),
          t(
            "Mandoob:Plans.FeatureValidity",
            formatNumber(plan.expiryDays, language),
          ),
        ].map((feature) => (
          <li
            key={feature}
            className="tw:flex tw:items-start tw:gap-2 tw:text-sm tw:text-foreground/80"
          >
            <Check
              className="tw:mt-0.5 tw:size-4 tw:shrink-0 tw:text-success"
              strokeWidth={2.5}
            />
            {feature}
          </li>
        ))}
      </ul>
      <Button
        type="button"
        onClick={onBuy}
        loading={buying}
        className="tw:mt-auto tw:w-full"
      >
        {t("Mandoob:Plans.Buy")}
      </Button>
    </div>
  );
}

export function PlansPage() {
  const { t, dir } = useLanguage();
  const session = useSession();
  // Native app with DISABLE_APP_PAYMENTS: purchases move to the website; the
  // web client ignores the flag entirely.
  const paymentsHidden = !!session?.appPaymentsDisabled && isNative();
  const plansQuery = useGetPlans({
    query: { enabled: !paymentsHidden && !PLANS_HIDDEN },
  });

  const checkout = useCheckout({
    mutation: {
      onSuccess: (result) => {
        if (isNative()) {
          // System browser (Custom Tab), not the app WebView — the success
          // page there deep-links back into the app when done.
          void import("@capacitor/browser").then(({ Browser }) =>
            Browser.open({ url: result.checkoutUrl }),
          );
          return;
        }
        window.location.assign(result.checkoutUrl);
      },
      onError: (error) => toast.error(errorMessage(error, t)),
    },
  });

  if (PLANS_HIDDEN) {
    return (
      <div dir={dir} className="tw:min-h-0 tw:flex-1 tw:overflow-y-auto">
        <div className="tw:mx-auto tw:flex tw:w-full tw:flex-col tw:gap-4 tw:px-4 tw:pb-6 tw:pt-[calc(20px+env(safe-area-inset-top))] tw:md:max-w-5xl tw:md:px-8 tw:md:pt-8">
          <OfferBanner variant="app" />

          <header>
            <h1 className="tw:m-0 tw:text-xl tw:font-semibold">
              {t("Mandoob:Plans.Title")}
            </h1>
            <p className="tw:m-0 tw:mt-1 tw:text-sm tw:text-muted-foreground">
              {t("Mandoob:Plans.Subtitle")}
            </p>
          </header>

          <ContactToBuyCard />
        </div>
      </div>
    );
  }

  if (paymentsHidden) {
    return (
      <div
        dir={dir}
        className="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:items-center tw:justify-center tw:gap-3 tw:p-8 tw:text-center"
      >
        <div className="tw:flex tw:size-16 tw:items-center tw:justify-center tw:rounded-full tw:bg-brand-50">
          <Globe className="tw:size-7 tw:text-brand" strokeWidth={1.5} />
        </div>
        <p className="tw:m-0 tw:text-lg tw:font-semibold">
          {t("Mandoob:Plans.NativeTitle")}
        </p>
        <p className="tw:m-0 tw:max-w-xs tw:text-sm tw:text-muted-foreground">
          {t("Mandoob:Plans.NativeSubtitle")}
        </p>
      </div>
    );
  }

  const plans = plansQuery.data ?? [];
  // The largest plan (Pro) gets the "best value" spotlight.
  const bestValueId =
    plans.length > 1
      ? plans.reduce((best, p) => (p.credits > best.credits ? p : best)).id
      : null;

  return (
    <div dir={dir} className="tw:min-h-0 tw:flex-1 tw:overflow-y-auto">
      <div className="tw:mx-auto tw:flex tw:w-full tw:flex-col tw:gap-4 tw:px-4 tw:pb-6 tw:pt-[calc(20px+env(safe-area-inset-top))] tw:md:max-w-5xl tw:md:px-8 tw:md:pt-8">
        <OfferBanner variant="app" />

        <header>
          <h1 className="tw:m-0 tw:text-xl tw:font-semibold">
            {t("Mandoob:Plans.Title")}
          </h1>
          <p className="tw:m-0 tw:mt-1 tw:text-sm tw:text-muted-foreground">
            {t("Mandoob:Plans.Subtitle")}
          </p>
        </header>

        {plansQuery.isLoading ? (
          <div className="tw:grid tw:gap-3 tw:md:grid-cols-2 tw:lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="tw:h-36 tw:w-full tw:rounded-xl" />
            ))}
          </div>
        ) : plans.length === 0 ? (
          <p className="tw:m-0 tw:rounded-xl tw:border tw:border-dashed tw:border-border tw:px-4 tw:py-10 tw:text-center tw:text-sm tw:text-muted-foreground">
            {t("Mandoob:Plans.NoPlans")}
          </p>
        ) : (
          <div className="tw:grid tw:gap-3 tw:pt-3 tw:md:grid-cols-2 tw:lg:grid-cols-3">
            {plans.map((plan, index) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                bestValue={plan.id === bestValueId}
                accent={index === plans.length - 1}
                buying={
                  checkout.isPending && checkout.variables?.planId === plan.id
                }
                onBuy={() =>
                  checkout.mutate({
                    planId: plan.id,
                    ...(isNative() ? { source: "app" as const } : {}),
                  })
                }
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
